/**
 * USB Device Simulator
 *
 * Permite simular la conexión y desconexión de dispositivos USB monitorizados
 * (Magic Leap 2) sin disponer del hardware real.
 *
 * La simulación se resuelve en el main process de Electron: el evento sintético
 * entra por el mismo punto del pipeline que el hotplug físico
 * (UsbDeviceManager → broadcast IPC → useUsbDevice → UI), así que todo lo que
 * hay aguas abajo se comporta igual que con las gafas conectadas. La única
 * diferencia observable es el flag `simulated: true` en el dispositivo.
 *
 * Solo funciona dentro de Electron; en navegador los métodos avisan y no hacen
 * nada.
 *
 * Uso desde la consola de DevTools:
 *   __ml2Simulator.connect()      // simular conexión de las gafas
 *   __ml2Simulator.disconnect()   // simular desconexión
 *   __ml2Simulator.toggle()       // alternar según el estado actual
 *   __ml2Simulator.status()       // ver dispositivos conectados
 */

import type {
  UsbDeviceType,
  UsbDeviceInfo,
  UsbDeviceSimulateResult,
} from '../../types/electron/usb-device.types';

/** Tipo de dispositivo simulado por defecto */
const DEFAULT_DEVICE: UsbDeviceType = 'magic-leap-2';

export class UsbDeviceSimulator {
  /**
   * Envía la petición de simulación al main process.
   * Devuelve null si la API de Electron no está disponible.
   */
  private async send(
    type: 'connected' | 'disconnected' | 'toggle',
    deviceType: UsbDeviceType,
  ): Promise<UsbDeviceSimulateResult | null> {
    const api = window.electron?.usbDevice;

    if (!api?.simulate) {
      console.warn(
        '[UsbDeviceSimulator] La API de dispositivos USB no está disponible. ' +
        'La simulación solo funciona dentro de la aplicación de escritorio (Electron).'
      );
      return null;
    }

    const result = await api.simulate(type, deviceType);

    if (result.applied) {
      console.log(
        `[UsbDeviceSimulator] ✔ ${deviceType} → ${result.connected ? 'CONECTADO' : 'DESCONECTADO'} (simulado)`
      );
    } else {
      console.warn(`[UsbDeviceSimulator] Sin cambios: ${result.reason ?? 'motivo desconocido'}`);
    }

    return result;
  }

  /** Simula la conexión del dispositivo */
  connect(deviceType: UsbDeviceType = DEFAULT_DEVICE) {
    return this.send('connected', deviceType);
  }

  /** Simula la desconexión del dispositivo */
  disconnect(deviceType: UsbDeviceType = DEFAULT_DEVICE) {
    return this.send('disconnected', deviceType);
  }

  /** Alterna entre conectado y desconectado */
  toggle(deviceType: UsbDeviceType = DEFAULT_DEVICE) {
    return this.send('toggle', deviceType);
  }

  /** Muestra por consola los dispositivos USB monitorizados conectados */
  async status() {
    const api = window.electron?.usbDevice;

    if (!api) {
      console.warn('[UsbDeviceSimulator] API de dispositivos USB no disponible (¿estás en Electron?)');
      return null;
    }

    const devices: UsbDeviceInfo[] = await api.getConnected();

    if (devices.length === 0) {
      console.log('[UsbDeviceSimulator] No hay dispositivos monitorizados conectados.');
    } else {
      console.table(devices.map((d: UsbDeviceInfo) => ({
        dispositivo: d.productName,
        tipo: d.deviceType,
        VID: `0x${d.vendorId.toString(16).toUpperCase().padStart(4, '0')}`,
        PID: `0x${d.productId.toString(16).toUpperCase().padStart(4, '0')}`,
        serie: d.serialNumber ?? '—',
        origen: d.simulated ? 'SIMULADO' : 'hardware',
      })));
    }

    return devices;
  }
}

// ---------------------------------------------------------------------------
// Registrar en window para acceso desde DevTools
// ---------------------------------------------------------------------------

const simulatorInstance = new UsbDeviceSimulator();

(window as any).__ml2Simulator = simulatorInstance;

console.log(
  '[UsbDeviceSimulator] Disponible en consola:\n' +
  '  __ml2Simulator.connect()     → Simular conexión de las Magic Leap 2\n' +
  '  __ml2Simulator.disconnect()  → Simular desconexión\n' +
  '  __ml2Simulator.toggle()      → Alternar estado\n' +
  '  __ml2Simulator.status()      → Ver dispositivos conectados'
);
